import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/baseUi/carousel"
import backgroundProfile from "@images/backgroundProfile.png"
import { word } from "@modules/core/utils/i18n"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramFilter } from "@modules/programs/types/programTypes"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { useCallback } from "react"
import { Text } from "@/components/ui/baseUi/text"

export function ProfilePrograms({ openProgramDisabled }: { openProgramDisabled: boolean }) {
  const { data: userPrograms, isLoading: loadingUserPrograms } = usePrograms({ programFilter: ProgramFilter.OWNED })
  const { data: subscribedPrograms, isLoading: loadingSubscribedPrograms } = usePrograms({
    programFilter: ProgramFilter.SUBSCRIBED,
  })
  const { data: sharedCoachPrograms, isLoading: loadingSharedCoachPrograms } = usePrograms({
    programFilter: ProgramFilter.SHARED_COACH,
  })

  const programCardCarrouselTemplate = useCallback(
    (program: UserProgramItemEntity, hideTitle: boolean) => {
      if (userPrograms.content.findIndex((userProgram) => userProgram.id === program.id) >= 0) {
        return (
          <>
            {!hideTitle && (
              <div className="mb-1 w-48">
                <Text fontStyle="LS" className="text-start">
                  {word("program.label.author")}
                </Text>
              </div>
            )}
            <HuProgramMiniCards item={program} disabled={openProgramDisabled} />
          </>
        )
      }
      if (sharedCoachPrograms.content.findIndex((sharedCoachProgram) => sharedCoachProgram.id === program.id) >= 0) {
        const index = sharedCoachPrograms.content.findIndex((p) => p.id === program.id)
        const shouldDisplayFirstItemLine = index === 0 && userPrograms.content.length > 0 && !hideTitle
        return (
          <div className={"flex flex-col items-center " + (shouldDisplayFirstItemLine ? "first-item" : "")}>
            {!hideTitle && (
              <div className="mb-1 w-48">
                <Text fontStyle="LS">{word("program.sharedCoach.title")}</Text>
              </div>
            )}

            <HuProgramMiniCards item={program} disabled={openProgramDisabled} />
          </div>
        )
      }

      return <HuProgramMiniCards item={program} disabled={openProgramDisabled} />
    },
    [userPrograms, sharedCoachPrograms],
  )

  if (loadingUserPrograms || loadingSubscribedPrograms || loadingSharedCoachPrograms) return
  if (
    userPrograms.content.length === 0 &&
    subscribedPrograms.content.length === 0 &&
    sharedCoachPrograms.content.length === 0
  )
    return (
      <div
        className="hidden h-full w-full rounded-4xl bg-surface-foreground bg-repeat lg:block"
        style={{ backgroundImage: `url(${backgroundProfile})` }}
      />
    )

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col">
      {(userPrograms.content.length > 0 || sharedCoachPrograms.content.length > 0) && (
        <div className="mb-6 w-full overflow-hidden">
          <Text fontStyle="TM">{word("publicProfile.userPersonalPrograms")}</Text>
          <div className="mt-2 flex gap-2">
            <Carousel className="mx-auto w-7/10" opts={{ align: "start", loop: true }}>
              <CarouselContent>
                {userPrograms.content.concat(sharedCoachPrograms.content).map((item: UserProgramItemEntity) => (
                  <CarouselItem className="basis-44" key={item.id}>
                    {programCardCarrouselTemplate(item, false)}
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </div>
      )}
      {subscribedPrograms.content.length > 0 && (
        <div className="flex flex-col gap-2">
          <Text fontStyle="TM">{word("publicProfile.followedPrograms")}</Text>
          <div className="flex gap-2">
            <Carousel className="mx-auto w-7/10" opts={{ align: "start", loop: true }}>
              <CarouselContent>
                {subscribedPrograms.content.map((item: UserProgramItemEntity) => (
                  <CarouselItem className="basis-44" key={item.id}>
                    {programCardCarrouselTemplate(item, true)}
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </div>
      )}
    </div>
  )
}
